import { ProactiveRaw, checkRaw, ChatTopLink, isChatTopLink } from "./lib/state/proactivesSlice.js";
import OmnichannelConfig from "@microsoft/omnichannel-chat-sdk/lib/core/OmnichannelConfig.js";
import ChatSDKConfig from "@microsoft/omnichannel-chat-sdk/lib/core/ChatSDKConfig.js";
import * as Sentry from "@sentry/react";
const userGroupsKey = "dpj_widget_userGroups";
const stateKey = "dpj_widget";
const activeWidgetConfKey = "dpj_widget_activeWidgetConfig";

type UserGroup =
    | "employee"
    | "copilot_test"

type WidgetConfig = OmnichannelConfig & { name: string }

function isUserGroup(group: string): group is UserGroup {
    return ["employee", "copilot_test"].includes(group);
}


// type Cart = {
//     "products_count": number,
//     "products_total": number,
//     "products": Array<{
//         "reference": string,
//         "name": string,
//         "quantity": number,
//         "total": number
//     }>
// }

// type Customer = {
//     "email": string,
//     "firstname": string,
//     "lastname": string,
//     "is_employee": boolean,
// }

function userGroups_get(): UserGroup[] {
    const userGroups = localStorage.getItem(userGroupsKey);
    if (userGroups) {
        return JSON.parse(userGroups);
    }
    return [];
}

function userGroups_has(group: UserGroup): boolean {
    const userGroups = userGroups_get()
    return userGroups.includes(group);
}

function userGroups_set(userGroups: UserGroup[]): void {
    if (!Array.isArray(userGroups) || userGroups.length === 0) {
        localStorage.removeItem(userGroupsKey);
        return;
    }
    localStorage.setItem(userGroupsKey, JSON.stringify(userGroups));
}

function userGroups_diff(userGroupsOld: UserGroup[], userGroupsNew: UserGroup[]): {
    added: UserGroup[]
    removed: UserGroup[]
} {
    const added = userGroupsNew.filter((group) => !userGroupsOld.includes(group));
    const removed = userGroupsOld.filter((group) => !userGroupsNew.includes(group));
    return { added, removed };
}

function chat_is_active(): boolean {
    const stateString = localStorage.getItem(stateKey);
    if (!stateString) {
        return false;
    }
    try {
        const state = JSON.parse(stateString);
        return state.state.context !== null;
    } catch (error) {
        console.error("Error parsing state", error)
        return false;
    }
}

function activeWidgetConf_set(widgetConf: null | WidgetConfig): void {
    if (widgetConf === null) {
        localStorage.removeItem(activeWidgetConfKey);
    } else {
        localStorage.setItem(activeWidgetConfKey, JSON.stringify(widgetConf));
    }
}

function activeWidgetConf_get(): null | WidgetConfig {
    const widgetConfString = localStorage.getItem(activeWidgetConfKey);
    if (!widgetConfString) {
        return null;
    }
    const conf = assertDataWidget(JSON.parse(widgetConfString));
    if (!conf) {
        activeWidgetConf_set(null)
        return null;
    }
    return conf;
}


async function dynamicConfig(): Promise<{ omni: Array<WidgetConfig>, chat: ChatSDKConfig, chatTopLinks: Array<ChatTopLink>, proactives: Array<ProactiveRaw> }> {

    // Create variables to store the data

    // Fetch the dynamic data
    let baseUrl = "";
    if (window.location.hostname === "www.dpj-workspace.com") {
        if (window.location.pathname.split("/").length >= 2) {
            baseUrl = `${window.location.origin}/${window.location.pathname.split("/")[1]}`
        } else {
            console.error("Invalid pathname", window.location.pathname)
            baseUrl = "https://www.dpj-workspace.com/de";
        }
    } else {
        baseUrl = window.location.origin;
    }

    const response = await fetch(`${baseUrl}/index.php?fc=module&module=dpjwidget&controller=data`).catch((error) => {
        console.error("Fetch error in dynamic data fetch:", error);
        throw error;
    })
    const dataUnknown: unknown = await response.json().catch((error) => {
        console.error("JSON error in dynamic data fetch:", error);
        throw error;
    })

    // Validate the data
    //const  dataCart = assertDataCart(dataUnknown)
    //const  dataCustomer = assertDataCustomer(dataUnknown)
    const dataProactives = assertDataProactives(dataUnknown)
    const dataUserGroups = assertDataUserGroups(dataUnknown)
    const dataWidgetConfigs = assertDataWidgetList(dataUnknown)
    const dataChatTopLinks = assertDataChatTopLinks(dataUnknown)
    // persist relevant data to local storage
    const userGroupsOld = userGroups_get()
    const { added, removed } = userGroups_diff(userGroupsOld, dataUserGroups as UserGroup[])
    userGroups_set(dataUserGroups as UserGroup[])
    const userGroupsNew = userGroups_get()
    console.debug("userGroups_diff", { added, removed, userGroups: userGroupsNew })

    // add proactives to window object (overwrites colliding ids)
    window.dpj = window.dpj || {};
    window.dpj.widget = window.dpj.widget || {};
    if (!dataUnknown || typeof dataUnknown !== "object" || !("proactives" in dataUnknown) || !Array.isArray(dataUnknown.proactives)) {
        throw new Error("Missing or invalid proactives");
    }
    const proactivesWindow = (window.dpj.widget.proactives || []).filter(checkRaw)
    const proactivesMap = new Map<string, ProactiveRaw>();
    proactivesWindow.forEach((p) => {
        proactivesMap.set(p.id, p);
    });
    dataProactives.forEach((p) => {
        proactivesMap.set(p.id, p);
    });
    window.dpj.widget.proactives = Array.from(proactivesMap.values());
    if (dataUnknown.proactives.length !== dataProactives.length) {
        console.error("Invalid proactives in dynamic config", dataUnknown.proactives.filter((p) => !checkRaw(p)))
    }

    // add chat top links to window object
    window.dpj.widget.chat_top_links = dataChatTopLinks


    // if we have an active context and configwe should keep using the same widget id
    const activeWidgetConf = activeWidgetConf_get()
    if (activeWidgetConf && chat_is_active()) {
        dataWidgetConfigs.unshift(activeWidgetConf)
    }

    // SDK CONFIG
    const chat: ChatSDKConfig = {
        dataMasking: {
            disable: false,
            maskingCharacter: "#",
        },
        telemetry: {
            disable: true,
        },
    };
    return { omni: dataWidgetConfigs, chat, chatTopLinks: dataChatTopLinks, proactives: dataProactives }
}


// Make sure the data is the correct type

function assertDataWidgetList(data: unknown): Array<WidgetConfig> {
    if (
        !data ||
        typeof data !== "object" ||
        !("widget" in data) ||
        !Array.isArray(data.widget) ||
        data.widget.length === 0
    ) {
        console.error("Missing or invalid widget fields", data)
        throw new Error("Missing or invalid widget field");
    }
    return data.widget.map(assertDataWidget).filter((w) => w !== null)
}

function assertDataWidget(data: unknown): null | WidgetConfig {
    if (
        !data ||
        typeof data !== "object" ||
        !("orgId" in data) ||
        !data.orgId ||
        typeof data.orgId !== "string" ||
        !("orgUrl" in data) ||
        !data.orgUrl ||
        typeof data.orgUrl !== "string" ||
        !("widgetId" in data) ||
        !data.widgetId ||
        typeof data.widgetId !== "string" ||
        !("name" in data) ||
        typeof data.name !== "string"
    ) {
        console.error("Missing or invalid field in widget config", data)
        Sentry.captureException(new Error("Missing or invalid field in widget config"), {
            extra: {
                widget: data
            }
        })
        return null
    }
    return {
        "orgId": data.orgId,
        "orgUrl": data.orgUrl,
        "widgetId": data.widgetId,
        "name": data.name,
    }
}

// function assertDataCart(data: unknown): null | Cart {
//     if (
//         data === null ||
//         typeof data !== "object" ||
//         !("cart" in data)
//     ) {
//         console.error("Missing or invalid cart", data)
//         throw new Error("Missing or invalid cart");
//     }
//     if (data.cart === null) {
//         return null
//     }

//     if (
//         typeof data.cart !== "object" ||
//         !("products_count" in data.cart) ||
//         typeof data.cart.products_count !== "number" ||
//         !("products_total" in data.cart) ||
//         typeof data.cart.products_total !== "number" ||
//         !("products" in data.cart) ||
//         !Array.isArray(data.cart.products)
//     ) {
//         console.error("Missing or invalid cart", data)
//         throw new Error("Missing or invalid cart");
//     }
//     return {
//         "products_count": data.cart.products_count,
//         "products_total": data.cart.products_total,
//         "products": data.cart.products.map((product) => ({
//             "reference": product.reference,
//             "name": product.name,
//             "quantity": product.quantity,
//             "total": product.total
//         }))
//     }
// }

// function assertDataCustomer(data: unknown): null | Customer {
//     if (
//         data === null ||
//         typeof data !== "object" ||
//         !("customer" in data)
//     ) {
//         throw new Error("Missing or invalid customer");
//     }
//     if (data.customer === null) {
//         return null
//     }

//     if (
//         typeof data.customer !== "object" ||
//         !("email" in data.customer) ||
//         typeof data.customer.email !== "string" ||
//         !("firstname" in data.customer) ||
//         typeof data.customer.firstname !== "string" ||
//         !("lastname" in data.customer) ||
//         typeof data.customer.lastname !== "string" ||
//         !("is_employee" in data.customer) ||
//         typeof data.customer.is_employee !== "boolean"
//     ) {
//         throw new Error("Missing or invalid customer");
//     }
//     return {
//         "email": data.customer.email,
//         "firstname": data.customer.firstname,
//         "lastname": data.customer.lastname,
//         "is_employee": data.customer.is_employee
//     }
// }

function assertDataUserGroups(data: unknown): UserGroup[] {
    if (
        !data ||
        typeof data !== "object" ||
        !("userGroups" in data) ||
        !Array.isArray(data.userGroups)
    ) {
        console.error("Missing or invalid userGroups", data)
        throw new Error("Missing or invalid userGroups");
    }
    if (data.userGroups.filter((g) => !isUserGroup(g)).length > 0) {
        console.error("Invalid user_groups", data.userGroups.filter((g) => !isUserGroup(g)))
        Sentry.captureException(new Error("Invalid user_groups"), {
            extra: {
                userGroups: data.userGroups
            }
        })
    }
    return data.userGroups as UserGroup[]
}

function assertDataProactives(data: unknown): Array<ProactiveRaw> {
    if (
        !data ||
        typeof data !== "object" ||
        !("proactives" in data) ||
        !Array.isArray(data.proactives)
    ) {
        console.error("Missing or invalid proactives", data)
        throw new Error("Missing or invalid proactives");
    }

    const filteredProactives = data.proactives.filter(checkRaw)
    if (filteredProactives.length !== data.proactives.length) {
        console.error("Invalid proactives", data.proactives.filter((p) => !checkRaw(p)))
    }
    return filteredProactives
}

function assertDataChatTopLinks(data: unknown): Array<ChatTopLink> {
    if (
        !data ||
        typeof data !== "object" ||
        !("chatTopLinks" in data) ||
        !Array.isArray(data.chatTopLinks)
    ) {
        console.error("Missing or invalid chatTopLinks", data)
        throw new Error("Missing or invalid chatTopLinks");
    }

    return data.chatTopLinks.filter(isChatTopLink)
}

export type { UserGroup, WidgetConfig }
export { dynamicConfig, activeWidgetConf_set, userGroups_has, activeWidgetConf_get }
